import { cva, type VariantProps } from 'class-variance-authority';
import { motion, AnimatePresence } from 'motion/react';
import React, { useState, useRef, useEffect } from 'react';
import { CheckCircle2, XCircle, AlertTriangle, Info, X } from 'lucide-react';
import { cn } from '../platform/dom/cn';

// CVA variants for toast styling
const toastVariants = cva(
  [
    'grid grid-cols-[auto_1fr_auto] gap-3 p-4 border rounded-lg shadow-lg max-w-200 min-w-100',
    'font-[Nunito_Sans]', // Using Nunito Sans font
    'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
  ],
  {
    variants: {
      type: {
        success: '',
        error: '',
        warning: '',
        info: '',
        default: ''
      }
    },
    defaultVariants: {
      type: 'default'
    }
  }
);

// CVA variants for icon colors
const iconVariants = cva('self-start [:where(&_svg)]:size-5', {
  variants: {
    type: {
      success: 'text-teal-500 dark:text-teal-400',
      error: 'text-rose-500 dark:text-rose-400',
      warning: 'text-amber-500 dark:text-amber-400',
      info: 'text-sky-500 dark:text-sky-400',
      default: 'text-gray-500 dark:text-gray-400'
    }
  },
  defaultVariants: {
    type: 'default'
  }
});

// CVA variants for action buttons
const actionVariants = cva('text-sm font-semibold transition-colors', {
  variants: {
    variant: {
      primary:
        'text-brand-500 dark:text-brand-300 hover:text-brand-800 dark:hover:text-brand-200'
    }
  },
  defaultVariants: {
    variant: 'primary'
  }
});

// CVA variants for close button
const closeVariants = cva(
  'self-start size-5 flex items-center justify-center transition-colors',
  {
    variants: {
      variant: {
        default: 'text-gray-400 hover:text-gray-600 dark:hover:text-gray-300'
      }
    },
    defaultVariants: {
      variant: 'default'
    }
  }
);

export interface ToastProps
  extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof toastVariants> {
  children: React.ReactNode;
  icon?: React.ReactNode;
  action?:
    | React.ReactNode
    | {
        label: React.ReactNode;
        onClick: (
          event: React.MouseEvent<HTMLButtonElement, MouseEvent>
        ) => void;
      };
  onClose?: () => void;
  onCloseAll?: () => void; // Function to close all toasts
  dark?: boolean; // Optional dark mode prop
}

const Toast: React.FC<ToastProps> = ({
  children,
  type = 'default',
  icon,
  action,
  onClose,
  onCloseAll,
  className,
  dark = false,
  ...props
}) => {
  const [isHovering, setIsHovering] = useState(false);
  const [showCloseAll, setShowCloseAll] = useState(false);
  const hoverTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Handle hover expansion with 500ms delay
  useEffect(() => {
    if (isHovering) {
      hoverTimeoutRef.current = setTimeout(() => {
        setShowCloseAll(true);
      }, 800);
    } else {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
        hoverTimeoutRef.current = null;
      }
      setShowCloseAll(false);
    }

    return () => {
      if (hoverTimeoutRef.current) {
        clearTimeout(hoverTimeoutRef.current);
      }
    };
  }, [isHovering]);
  const getDefaultIcon = () => {
    switch (type) {
      case 'success':
        return <CheckCircle2 />;
      case 'error':
        return <XCircle />;
      case 'warning':
        return <AlertTriangle />;
      case 'info':
        return <Info />;
      default:
        return null;
    }
  };

  const renderAction = () => {
    if (!action) return null;

    if (
      typeof action === 'object' &&
      'label' in action &&
      'onClick' in action
    ) {
      return (
        <button
          onClick={e => {
            action.onClick(e);
            // Automatically close toast after action is clicked
            if (onClose) {
              onClose();
            }
          }}
          className={actionVariants()}
        >
          {action.label}
        </button>
      );
    }

    return action;
  };

  const hasIcon = icon || type !== 'default';

  return (
    <div className={cn('tailwind-root contents', dark && 'dark')}>
      <div className={cn(toastVariants({ type }), className)} {...props}>
        {/* Icon - Grid Column 1 */}
        {hasIcon && (
          <div className={iconVariants({ type })}>
            {icon || getDefaultIcon()}
          </div>
        )}

        {/* Content - Grid Column 2 */}
        <div
          className={cn(
            'min-w-0 flex flex-col justify-start gap-3',
            !hasIcon && 'col-span-2'
          )}
        >
          <div className="text-gray-900 dark:text-gray-100 text-md font-medium leading-tight">
            {children}
          </div>
          {renderAction() && (
            <div className="flex items-center gap-2">{renderAction()}</div>
          )}
        </div>

        {/* Close button area - Grid Column 3 */}
        {onClose && (
          <div
            className="self-start relative"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            {/* Clear all button - absolutely positioned to the left */}
            <AnimatePresence initial={false}>
              {showCloseAll && onCloseAll && (
                <div className="absolute right-full h-full w-20 flex items-center justify-end pr-1">
                  <motion.button
                    initial={{ opacity: 0, x: 10 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 10 }}
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                    onClick={e => {
                      e.stopPropagation();
                      onCloseAll();
                    }}
                    className="px-3 py-0.5 text-xs font-medium text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-full transition-colors whitespace-nowrap"
                    aria-label="Clear all"
                  >
                    Clear all
                  </motion.button>
                </div>
              )}
            </AnimatePresence>

            {/* Main close button - always visible */}
            <button
              onClick={e => {
                e.stopPropagation();
                onClose();
              }}
              className={cn(
                closeVariants(),
                'p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors'
              )}
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Toast;

// Export variants for potential reuse
export { toastVariants, iconVariants, actionVariants, closeVariants };
